import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CACHE_TTL_MS } from 'src/app/core/http/http-context.tokens';
import { IApiResponse } from '../utils/common.interface';

/** Calendar periods in UTC, as the backend computes them. */
export type IntroducerPeriod = 'all_time' | 'this_month' | 'last_year' | 'last_month' | 'last_week' | 'yesterday' | 'today';

export type IntroducerPeriodTotals = Record<IntroducerPeriod, number>;

export interface IntroducerOverview {
    is_introducer: boolean;
    total_earned: number;
    people_introduced: number;
}

export interface IntroducerSummary {
    is_introducer: boolean;
    timezone: string;
    totals: IntroducerPeriodTotals;
    /** Fee events behind each total. */
    events: IntroducerPeriodTotals;
    /** Month to date vs the same elapsed span of last month; null when last month had nothing. */
    vs_last_month_pct: number | null;
    people_introduced: number;
    active_people: number;
    activities: number;
    by_currency: { symbol: string | null; display_symbol: string | null; all_time: number }[];
}

export interface IntroducerSeriesPoint {
    period_start: string;
    amount: number;
}

export interface IntroducedUser {
    user_id: string;
    name: string | null;
    wallet_address: string | null;
    /** `ended` once this user's introducer was changed or removed. */
    status: 'active' | 'ended';
    added_on: string | null;
    fee_share_pct: number | null;
    applied_to: { enabled: number; total: number } | null;
    activities: number;
    last_earned_at: string | null;
    earned: IntroducerPeriodTotals;
}

export interface IntroducerTransaction {
    _id: string;
    date: string;
    event_type: string;
    fee_type: string;
    loan_type: 'new' | 'refinance' | null;
    gross_fee: number;
    commission: number;
    currency_symbol: string | null;
    transaction_hash: string | null;
    reference: { type: string | null; id: string | null };
    nft_names: string[];
}

export interface IntroducerPage<T> {
    docs: T[];
    totalDocs: number;
    limit: number;
    page: number;
    totalPages: number;
    period: IntroducerPeriod;
    timezone: string;
    total_commission?: number;
}

export interface IntroducedUsersQuery {
    page: number;
    limit: number;
    period: IntroducerPeriod;
    search?: string;
    sort?: 'added_on' | 'earned' | 'name';
    order?: 'asc' | 'desc';
}

/** The header asks on every load, so a short cache keeps navigation from refetching it. */
const OVERVIEW_CACHE_TTL_MS = 30_000;

@Injectable({ providedIn: 'root' })
export class IntroducerService {
    private readonly baseUrl = `${environment.API_BASE_URL_V3}/introducer`;

    constructor(private http: HttpClient) {}

    getOverview(): Observable<IApiResponse<IntroducerOverview>> {
        return this.http.get<IApiResponse<IntroducerOverview>>(`${this.baseUrl}/overview`, {
            context: new HttpContext().set(CACHE_TTL_MS, OVERVIEW_CACHE_TTL_MS),
        });
    }

    getSummary(): Observable<IApiResponse<IntroducerSummary>> {
        return this.http.get<IApiResponse<IntroducerSummary>>(`${this.baseUrl}/summary`);
    }

    getEarningsSeries(granularity: 'monthly' | 'weekly', count = 6): Observable<IApiResponse<{ series: IntroducerSeriesPoint[] }>> {
        const params = new HttpParams().set('granularity', granularity).set('count', count);
        return this.http.get<IApiResponse<{ series: IntroducerSeriesPoint[] }>>(`${this.baseUrl}/earnings-series`, { params });
    }

    getIntroducedUsers(query: IntroducedUsersQuery): Observable<IApiResponse<IntroducerPage<IntroducedUser>>> {
        let params = new HttpParams().set('page', query.page).set('limit', query.limit).set('period', query.period);
        if (query.search) params = params.set('search', query.search);
        if (query.sort) params = params.set('sort', query.sort);
        if (query.order) params = params.set('order', query.order);
        return this.http.get<IApiResponse<IntroducerPage<IntroducedUser>>>(`${this.baseUrl}/users`, { params });
    }

    getUserTransactions(userId: string, period: IntroducerPeriod, page: number, limit: number): Observable<IApiResponse<IntroducerPage<IntroducerTransaction>>> {
        const params = new HttpParams().set('id', userId).set('period', period).set('page', page).set('limit', limit);
        return this.http.get<IApiResponse<IntroducerPage<IntroducerTransaction>>>(`${this.baseUrl}/user-transactions`, { params });
    }
}
